import apiClient from './apiClient';
import type {
  QueueSnapshot,
  SnapshotMetricsResponse,
  PagedSnapshotMessagesResponse,
  SnapshotTriggerResponse,
  ActiveHistoryResponse,
  AggregatedActiveHistoryResponse,
  ActiveMetricsResponse
} from '../types';

/**
 * Service para interagir com a API de Snapshot de Filas
 *
 * O snapshot é capturado por uma Azure Function a cada hora e pode ser disparado manualmente.
 * Fornece dados completos (100% das mensagens) ao invés de amostragem.
 */
export const snapshotService = {
  /**
   * Obtém o snapshot atual completo (métricas + todas as mensagens)
   * ATENÇÃO: Pode ser um payload grande para filas com muitas mensagens
   */
  async getCurrent(): Promise<QueueSnapshot> {
    const response = await apiClient.get<QueueSnapshot>('/snapshot/current');
    return response.data;
  },

  /**
   * Obtém apenas as métricas do snapshot (mais leve, sem lista de mensagens)
   * Use este método para dashboards e visualizações rápidas
   */
  async getMetrics(): Promise<SnapshotMetricsResponse> {
    const response = await apiClient.get<SnapshotMetricsResponse>('/snapshot/metrics');
    return response.data;
  },

  /**
   * Lista mensagens DLQ do snapshot com paginação
   * @param page Número da página (começa em 1)
   * @param pageSize Tamanho da página (máximo 500)
   * @param messageTypeFilter Filtro por tipo de mensagem (opcional)
   * @param errorPatternFilter Filtro por padrão de erro (opcional)
   * @param searchText Texto de busca em ExternalId, CorrelationId, MessageId (opcional)
   */
  async getDlqMessages(
    page: number = 1,
    pageSize: number = 100,
    messageTypeFilter?: string,
    errorPatternFilter?: string,
    searchText?: string
  ): Promise<PagedSnapshotMessagesResponse> {
    const response = await apiClient.get<PagedSnapshotMessagesResponse>('/snapshot/dlq', {
      params: {
        page,
        pageSize,
        messageTypeFilter,
        errorPatternFilter,
        searchText
      }
    });
    return response.data;
  },

  /**
   * Lista mensagens Scheduled do snapshot com paginação e filtros
   * @param page Número da página (começa em 1)
   * @param pageSize Tamanho da página (máximo 500)
   * @param messageTypeFilter Filtro por tipo de mensagem (opcional)
   * @param searchText Texto de busca em vários campos (opcional)
   * @param minRetryCount Número mínimo de tentativas (opcional)
   * @param errorPatternFilter Filtro por tipo de erro/LastError (opcional)
   */
  async getScheduledMessages(
    page: number = 1,
    pageSize: number = 50,
    messageTypeFilter?: string,
    searchText?: string,
    minRetryCount?: number,
    errorPatternFilter?: string
  ): Promise<PagedSnapshotMessagesResponse> {
    const response = await apiClient.get<PagedSnapshotMessagesResponse>('/snapshot/scheduled', {
      params: {
        page,
        pageSize,
        messageTypeFilter,
        searchText,
        minRetryCount,
        errorPatternFilter
      }
    });
    return response.data;
  },

  /**
   * Lista mensagens Ativas DIRETAMENTE do Service Bus (tempo real)
   * @param page Numero da pagina (comeca em 1)
   * @param pageSize Tamanho da pagina (maximo 500)
   * @param messageTypeFilter Filtro por tipo de mensagem (opcional)
   * @param searchText Texto de busca em varios campos (opcional)
   */
  async getActiveMessages(
    page: number = 1,
    pageSize: number = 50,
    messageTypeFilter?: string,
    searchText?: string
  ): Promise<PagedSnapshotMessagesResponse> {
    const response = await apiClient.get<PagedSnapshotMessagesResponse>('/active', {
      params: {
        page,
        pageSize,
        messageTypeFilter,
        searchText
      }
    });
    return response.data;
  },

  /**
   * Retorna apenas a contagem de mensagens ativas (leve, para polling em tempo real)
   */
  async getActiveMessagesCount(): Promise<{ count: number; timestamp: string }> {
    const response = await apiClient.get<{ count: number; timestamp: string }>('/active/count');
    return response.data;
  },

  /**
   * Retorna metricas de mensagens ativas incluindo contagem de entrantes
   * Usado para calcular taxas de entrada e saida em tempo real
   */
  async getActiveMetrics(): Promise<ActiveMetricsResponse> {
    const response = await apiClient.get<ActiveMetricsResponse>('/active/metrics');
    return response.data;
  },

  /**
   * Retorna o historico de contagens de mensagens ativas das ultimas 24 horas
   * (granularidade de 1 minuto, persistido no Table Storage)
   */
  async getActiveMessagesHistory(): Promise<ActiveHistoryResponse> {
    const response = await apiClient.get<ActiveHistoryResponse>('/active/history');
    return response.data;
  },

  /**
   * Retorna historico agregado por hora + dados granulares da hora atual
   * Otimizado para graficos com visao de 24h + tempo real
   */
  async getAggregatedActiveMessagesHistory(): Promise<AggregatedActiveHistoryResponse> {
    const response = await apiClient.get<AggregatedActiveHistoryResponse>('/active/history/aggregated');
    return response.data;
  },

  /**
   * Dispara manualmente a captura de um novo snapshot
   * Chama diretamente a Azure Function (não passa pela API)
   * Use getMetrics() para verificar o status
   *
   * Requer configuração da variável de ambiente VITE_SNAPSHOT_FUNCTION_URL
   */
  async triggerCapture(): Promise<SnapshotTriggerResponse> {
    const functionUrl = import.meta.env.VITE_SNAPSHOT_FUNCTION_URL;
    const functionKey = import.meta.env.VITE_SNAPSHOT_FUNCTION_KEY;

    if (!functionUrl) {
      throw new Error('VITE_SNAPSHOT_FUNCTION_URL not configured');
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (functionKey) {
      headers['x-functions-key'] = functionKey;
    }

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger snapshot: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Polling helper - aguarda até que o snapshot seja completado
   * @param onUpdate Callback chamado a cada verificação de status
   * @param intervalMs Intervalo entre verificações (default: 3 segundos)
   * @param maxAttempts Máximo de tentativas (default: 60 = 3 minutos)
   */
  async pollUntilComplete(
    onUpdate: (metrics: SnapshotMetricsResponse) => void,
    intervalMs: number = 3000,
    maxAttempts: number = 60
  ): Promise<SnapshotMetricsResponse> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const metrics = await this.getMetrics();
        onUpdate(metrics);

        if (metrics.status === 'Completed' || metrics.status === 'Failed') {
          return metrics;
        }
      } catch (err: any) {
        // 404 durante polling significa que o snapshot ainda está sendo criado
        // Continua tentando
        if (err.response?.status !== 404 && !err.message?.includes('404')) {
          throw err;
        }
        console.log('Snapshot ainda não disponível, aguardando...');
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }

    throw new Error('Snapshot capture timeout - polling exceeded max attempts');
  },

  /**
   * Calcula estatísticas úteis a partir das métricas
   */
  getStats(metrics: SnapshotMetricsResponse) {
    const totalMessages = metrics.metrics.dlqCount + metrics.metrics.scheduledCount + metrics.metrics.activeCount;
    const dlqPercentage = totalMessages > 0
      ? ((metrics.metrics.dlqCount / totalMessages) * 100).toFixed(1)
      : '0';

    const topErrorPatterns = Object.entries(metrics.metrics.dlqByErrorPattern || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const topMessageTypes = Object.entries(metrics.metrics.dlqByMessageType || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    return {
      totalMessages,
      dlqPercentage,
      topErrorPatterns,
      topMessageTypes,
      sizeFormatted: formatBytes(metrics.metrics.totalSizeBytes)
    };
  }
};

/**
 * Helper para formatar bytes em unidades legíveis
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
